import React, { useCallback, useEffect, useState } from 'react';

import { Button, Stack, TextField, Table, Typography } from '@mui/material';
import api from '../../services/api';

import Box from '@mui/material/Box';
import { useParams } from 'react-router-dom';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SportsBaseballRoundedIcon from '@mui/icons-material/SportsBaseballRounded';
import ImportContactsRoundedIcon from '@mui/icons-material/ImportContactsRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import { toaster } from '../../services/toaster';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as DatePickerMUI } from '@mui/x-date-pickers/DatePicker/DatePicker';
import dayjs from 'dayjs';
import { renderTimeViewClock, TimePicker } from '@mui/x-date-pickers';

interface Props {}

export const ConfirmReservationPage: React.ComponentType<Props> = ({}) => {
  const { id } = useParams();

  const [state, setState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<any>({});
  const [buttonLoading, setButtonLoading] = useState<any>(false);
  const [selectedActionType, setSelectedActionType] = useState<any>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);

  const [offeredDate, setOfferedDate] = useState<any>(undefined);
  const [offeredTime, setOfferedTime] = useState<any>(undefined);

  const [signature, setSignature] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [accept, setAccept] = useState(false);
  const [decline, setDecline] = useState(false);
  const [stateSend, setStateSend] = useState(false);
  const [stateError, setStateError] = useState(false);
  const [completed, setCompleted] = useState(false);
  const handleCloseSend = () => {
    setStateSend(false);
  };

  const handleClose = () => {
    setState(false);
  };

  const handleCloseError = () => {
    setStateError(false);
  };

  const fetchReservationData = useCallback(() => {
    const searchParams = new URLSearchParams({});
    setLoading(true);
    api
      .get(`/guest/service-reservation/${id}`, searchParams)
      .then((res: any) => {
        setProperties(res.data);
        setLoading(false);

        if (res.data?.is_completed_by_provider) {
          setCompleted(true);
        }

        if (res.data?.response_from_provider) {
          const responseFromProvider = res.data?.response_from_provider;
          setSelectedActionType(responseFromProvider);
          if (responseFromProvider == 'accept') {
            setAccept(true);
          }
          if (responseFromProvider == 'decline') {
            setDecline(true);
          }
        }
      });
  }, []);

  useEffect(() => {
    fetchReservationData();
  }, []);

  const onSubmit = useCallback(
    (action: 'accept' | 'decline') => {
      setButtonLoading(action);
      setSelectedActionType(action);

      api
        .patch(`/guest/service-reservation/${properties?.id}`, {
          action: action,
          type: properties.type,
        })
        .then((result: any) => {
          toaster.show({
            intent: 'success',
            message:
              action === 'accept'
                ? 'Successfully accepted reservation'
                : 'Successfully declined reservation',
          });

          if (action === 'accept') {
            setAccept(true);
          }
          if (action === 'decline') {
            setDecline(true);
          }

          setButtonLoading(undefined);
        })
        .catch((error) => {});
    },
    [properties]
  );

  const onSubmitAfterData = useCallback(() => {
    if (!role || role.length < 2) {
      toaster.show({
        icon: 'error',
        message: `Error: 'The Position cannot be shorter than 2 characters'`,
        intent: 'danger',
      });
      return false;
    }
    if (!signature || signature.length < 4) {
      toaster.show({
        icon: 'error',
        message: `Error: 'The Signature cannot be shorter than 4 characters'`,
        intent: 'danger',
      });
      return false;
    }
    setButtonLoading('send');
    api
      .patch(`/guest/service-reservation/${properties?.id}`, {
        action: 'send_after_data',
        role: role,
        signature: signature,
      })
      .then((result: any) => {
        toaster.show({
          intent: 'success',
          message: 'Successfully sent confirmation',
        });
        fetchReservationData();
        setButtonLoading(undefined);
      })
      .catch((error) => {
        toaster.show({
          icon: 'error',
          message: `Error: Error while saving data'`,
          intent: 'danger',
        });
        setButtonLoading(undefined);
      })
      .finally(() => {});

    return true;
  }, [role, signature, properties, offeredDate, offeredTime]);

  const onSubmitDeclineAfterData = useCallback(() => {
    setButtonLoading('send');

    api
      .patch(`/guest/service-reservation/${properties?.id}`, {
        action: 'send_after_data_decline',
        justification: message,
        offered_date: offeredDate,
        offered_time: offeredTime,
      })
      .then((result: any) => {
        toaster.show({
          intent: 'success',
          message: 'Successfully sent confirmation',
        });
        fetchReservationData();
      })
      .catch((error) => {
        toaster.show({
          icon: 'error',
          message: `Error: Error while saving data'`,
          intent: 'danger',
        });
      })
      .finally(() => {
        setButtonLoading(undefined);
      });
  }, [message, properties, offeredDate, offeredTime]);

  return (
    <>
      <Box
        minHeight={'100vh'}
        width={'100vw'}
        sx={{
          background: 'linear-gradient(141deg, #256861, #86e0d6)',
        }}
      >
        <Box
          sx={{
            p: '20px',
            width: '100%',
            maxWidth: '600px',
            margin: {
              xs: '15px',
              md: '100px auto',
            },
            background: '#fff',
            boxShadow: '0px 10px 20px -4px #00000054',
            borderRadius: '15px',
          }}
        >
          <Table>
            <TableBody
              sx={{
                '& .MuiTableCell-root': {
                  color: '#464646',
                },
                '& .MuiTableCell-root .MuiSvgIcon-root': {
                  color: '#256861',
                  marginRight: '15px',
                },
              }}
            >
              <TableRow>
                <TableCell>
                  <GroupsRoundedIcon /> Group:
                </TableCell>
                <TableCell>{properties?.group_name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <SportsBaseballRoundedIcon /> Activity:
                </TableCell>
                <TableCell>{properties?.activity_name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <ImportContactsRoundedIcon /> Service:
                </TableCell>
                <TableCell>{properties?.service_name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <ManageAccountsRoundedIcon /> Service Provider:
                </TableCell>
                <TableCell>{properties?.service_provider_name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <EventAvailableRoundedIcon /> Date:
                </TableCell>
                <TableCell>{properties?.date_time}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {!completed && (
            <Box
              sx={{
                mt: '20px',
              }}
            >
              {!accept && !decline ? (
                <>
                  <Stack
                    gap={2}
                    sx={{
                      flexDirection: {
                        xs: 'column',
                        md: 'row',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: {
                          xs: '100%',
                          md: '50%',
                        },
                      }}
                    >
                      <Button
                        color={'error'}
                        variant={'outlined'}
                        fullWidth={true}
                        onClick={() => onSubmit('decline')}
                        disabled={buttonLoading}
                      >
                        {buttonLoading === 'decline' ? (
                          <>
                            <CircularProgress
                              size={20}
                              style={{ marginRight: 8 }}
                            />
                            Sending...
                          </>
                        ) : (
                          'Decline'
                        )}
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        width: {
                          xs: '100%',
                          md: '50%',
                        },
                      }}
                    >
                      <Button
                        color={'primary'}
                        variant={'contained'}
                        fullWidth={true}
                        onClick={() => onSubmit('accept')}
                        disabled={buttonLoading}
                      >
                        {buttonLoading === 'accept' ? (
                          <>
                            <CircularProgress
                              size={20}
                              style={{ marginRight: 8 }}
                            />
                            Sending...
                          </>
                        ) : (
                          'Accept'
                        )}
                      </Button>
                    </Box>
                  </Stack>
                </>
              ) : (
                <>
                  {selectedActionType === 'accept' && (
                    <>
                      <Stack>
                        <Typography variant={'caption'}>
                          Thank you! Succesfully <b>accepted</b> the reservation
                        </Typography>
                        <Typography
                          variant={'caption'}
                          sx={{
                            color: '#666',
                          }}
                        >
                          Please fill in the fields below:
                        </Typography>
                      </Stack>
                      <Stack
                        gap={2}
                        sx={{
                          flexDirection: {
                            xs: 'column',
                            md: 'row',
                          },
                          mt: '10px',
                        }}
                      >
                        <TextField
                          label="Signature"
                          value={signature}
                          fullWidth={true}
                          size={'small'}
                          onChange={(event) => {
                            setSignature(event?.target?.value);
                          }}
                        />
                        <TextField
                          label="Position"
                          value={role}
                          fullWidth={true}
                          size={'small'}
                          onChange={(event) => {
                            setRole(event?.target?.value);
                          }}
                        />
                        <Button
                          fullWidth={true}
                          variant="contained"
                          endIcon={<SendIcon />}
                          onClick={onSubmitAfterData}
                          disabled={buttonLoading === 'send'}
                        >
                          {buttonLoading === 'send' ? (
                            <>
                              <CircularProgress
                                size={20}
                                style={{ marginRight: 8 }}
                              />
                              Sending...
                            </>
                          ) : (
                            'Send'
                          )}
                        </Button>
                      </Stack>
                    </>
                  )}

                  {selectedActionType === 'decline' && (
                    <>
                      <Stack>
                        <Typography variant={'caption'}>
                          Thank you! Succesfully <b>declined</b> the reservation
                        </Typography>
                        <Typography
                          variant={'caption'}
                          sx={{
                            color: '#666',
                          }}
                        >
                          Please fill in the fields below:
                        </Typography>
                      </Stack>
                      <Stack
                        gap={2}
                        sx={{
                          flexDirection: {
                            xs: 'column',
                            md: 'row',
                          },
                          mt: '10px',
                        }}
                      >
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale={'en-gb'}
                        >
                          <DatePickerMUI
                            label={'Suggest another date (optional)'}
                            defaultValue={
                              properties?.date === undefined
                                ? null
                                : dayjs(properties?.date)
                            }
                            onChange={(value, context) => {
                              //onChange(dayjs(value, 'YYYY-MM-DD').format('YYYY-MM-DD'));
                              setOfferedDate(
                                dayjs(value, 'YYYY-MM-DD').format('YYYY-MM-DD')
                              );
                            }}
                            format={'YYYY-MM-DD'}
                          />
                        </LocalizationProvider>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            label={'Suggest another time (optional)'}
                            ampm={false}
                            defaultValue={dayjs(properties?.time, 'HH:mm')}
                            views={['hours', 'minutes']}
                            onChange={(newValue: any) => {
                              const datetime = dayjs(newValue, 'HH:mm').format(
                                'HH:mm'
                              );

                              if (datetime !== 'Invalid Date') {
                                setOfferedTime(
                                  dayjs(newValue, 'HH:mm').format('HH:mm')
                                );
                              } else {
                                setOfferedTime(null);
                              }
                            }}
                            viewRenderers={{
                              hours: renderTimeViewClock,
                              minutes: renderTimeViewClock,
                              seconds: renderTimeViewClock,
                            }}
                          />
                        </LocalizationProvider>
                      </Stack>

                      <Stack
                        gap={2}
                        sx={{
                          flexDirection: {
                            xs: 'column',
                            md: 'row',
                          },
                          mt: '10px',
                        }}
                      >
                        <TextField
                          label="Justification"
                          multiline
                          maxRows={4}
                          fullWidth={true}
                          onChange={(event) => {
                            setMessage(event?.target?.value);
                          }}
                        />
                        <Button
                          //fullWidth={true}
                          variant="contained"
                          endIcon={<SendIcon />}
                          onClick={onSubmitDeclineAfterData}
                          disabled={buttonLoading === 'send'}
                          sx={{
                            minWidth: {
                              sm: '100%',
                              md: '140px',
                            },
                          }}
                        >
                          {buttonLoading === 'send' ? (
                            <>
                              <CircularProgress
                                size={20}
                                style={{ marginRight: 8 }}
                              />
                              Sending...
                            </>
                          ) : (
                            'Send'
                          )}
                        </Button>
                      </Stack>
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
